
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const StateRequirementsPage = () => {
  useSEO({
    title: 'State Cybersecurity Requirements for Financial Firms | Mortgage Moat',
    description:
      'State-by-state cybersecurity and data protection requirements for mortgage and financial companies. Covers Michigan, North Carolina, Massachusetts, Oregon, Washington, and more.',
    keywords:
      'state cybersecurity requirements, mortgage compliance by state, GLBA state laws, financial firm data protection, state data breach laws',
    canonical: '/state-requirements',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'State Cybersecurity Requirements — Mortgage Moat',
        url: `${SITE_URL}/state-requirements`,
        description:
          'State-by-state cybersecurity and data protection requirements for mortgage and financial companies.',
        publisher: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'State Requirements',
            item: `${SITE_URL}/state-requirements`,
          },
        ],
      },
    ],
  });

  const [openState, setOpenState] = useState<string | null>(null);

  const toggleState = (state: string) => {
    setOpenState(openState === state ? null : state);
  };

  const states = [
    { id: 'michigan', name: 'Michigan', color: 'blue' },
    { id: 'north-carolina', name: 'North Carolina', color: 'indigo' },
    { id: 'massachusetts', name: 'Massachusetts', color: 'purple' },
    { id: 'oregon', name: 'Oregon', color: 'green' },
    { id: 'washington', name: 'Washington', color: 'teal' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                Compliance Resources
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                State Cybersecurity <span className="text-blue-600">Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                While federal regulations like GLBA apply nationwide, some states have enacted additional
                cybersecurity and data protection requirements that financial institutions must follow.
                Understanding these state‑specific rules is critical for maintaining compliance.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only.
                    State cybersecurity regulations are complex and subject to change. We recommend
                    reaching out to our team to confirm you have covered all applicable requirements for
                    the states where you operate.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* State Accordions */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">State-by-State Requirements</h2>
                <p className="text-xl text-gray-600">
                  Click on each state to view specific cybersecurity and data protection requirements
                </p>
              </div>

              <div className="space-y-4">
                {states.map((state) => (
                  <div
                    key={state.id}
                    className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => toggleState(state.id)}
                      className="w-full flex items-center justify-between p-6 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 bg-${state.color}-100 rounded-full flex items-center justify-center`}>
                          <i className={`ri-map-pin-line text-2xl text-${state.color}-600`}></i>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{state.name}</h3>
                      </div>
                      <i
                        className={`ri-arrow-${openState === state.id ? 'up' : 'down'}-s-line text-2xl text-gray-400`}
                      ></i>
                    </button>

                    {openState === state.id && (
                      <div className="p-6 bg-gray-50 border-t border-gray-200">
                        <div className="prose prose-sm max-w-none">
                          {/* Michigan Content */}
                          {state.id === 'michigan' && (
                            <div className="space-y-6">
                              <p className="text-gray-700">
                                Michigan has adopted an <strong>Insurance Data Security Law</strong> (added as
                                Chapter 5A to the Insurance Code, MCL 500.550–500.565) that imposes cybersecurity
                                requirements on licensed insurers, producers, and other "licensees" of the Department of
                                Insurance and Financial Services (DIFS). These rules are modeled on the NAIC Insurance
                                Data Security Model Law and require a formal, documented information security program
                                based on risk.
                              </p>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Who is covered?</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-1"></i>
                                    <span>Insurance carriers and producers licensed in Michigan</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Certain other entities licensed or registered under the Michigan Insurance Code
                                      ("licensees")
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Some small licensees have limited exemptions from certain obligations, but are
                                      still expected to protect nonpublic information
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">
                                  Core Cybersecurity Program Expectations
                                </h4>
                                <p className="text-gray-700 mb-3">
                                  Michigan's Insurance Data Security Law expects covered licensees to:
                                </p>

                                <div className="space-y-4">
                                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">
                                      Maintain a Written Information Security Program (ISP)
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Designed to protect the security and confidentiality of "nonpublic information"</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Appropriate to the licensee's size, complexity, and the sensitivity of information
                                          handled
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">
                                      Perform Periodic Risk Assessments
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Identify reasonably foreseeable internal and external threats</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Assess the likelihood and potential damage of those threats</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Evaluate the sufficiency of existing security controls</span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">
                                      Implement Administrative, Technical, and Physical Safeguards
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Access controls and authentication</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Secure development and change management practices</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Encryption or other compensating controls for data at rest and in transit where
                                          appropriate
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Multi-factor authentication where reasonable</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Secure disposal of nonpublic information in any format</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Monitoring systems for unauthorized access or tampering</span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">
                                      Vendor / Third-Party Oversight
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Exercise due diligence in selecting service providers</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Require providers by contract to maintain appropriate security safeguards</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Monitor providers' compliance as part of your risk management process</span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">
                                      Incident Response Planning
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Maintain a written incident response plan that describes roles, decision
                                          making, communication, investigation, containment, recovery, and documentation
                                          steps for cybersecurity events
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Ongoing Governance</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Periodic updates to the ISP as threats, vulnerabilities, business operations,
                                          and technology change
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Cybersecurity risks integrated into enterprise risk management</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          For certain licensees, board or senior management oversight and periodic reporting
                                          on the information security program
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Breach Notification and Regulator Reporting</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-1"></i>
                                    <span>Covered licensees must investigate cybersecurity events involving nonpublic information</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      If certain thresholds are met (e.g., impact on Michigan residents or the licensee's overall
                                      operations), notification to DIFS and affected consumers is required within specified
                                      time frames and with prescribed content
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-blue-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Additional federal or other state breach notice requirements may also apply depending on the
                                      nature of the data and where consumers reside
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* North Carolina Content */}
                          {state.id === 'north-carolina' && (
                            <div className="space-y-6">
                              <p className="text-gray-700">
                                North Carolina's <strong>Identity Theft Protection Act</strong> (mainly Article 2A of
                                Chapter 75 of the General Statutes) sets requirements for how businesses and government
                                entities protect personal information of North Carolina residents and how they respond to
                                security breaches.
                              </p>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Who is covered?</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Any business or state/local government agency that owns or licenses "personal
                                      information" about a North Carolina resident
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      This can include financial institutions, lenders, servicers, and other organizations
                                      handling sensitive consumer data
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Key Cybersecurity and Data Handling Expectations</h4>
                                <p className="text-gray-700 mb-3">
                                  While North Carolina's law is often discussed in the context of breach notification,
                                  it also embeds data security and identity‑theft prevention concepts, including:
                                </p>

                                <div className="space-y-4">
                                  <div className="bg-white rounded-lg p-4 border-l-4 border-indigo-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Reasonable Security Measures</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Businesses must take reasonable steps to protect personal information from
                                          unauthorized access, acquisition, or use that could result in harm or identity
                                          theft
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-indigo-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Restrictions on Social Security Numbers</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          SSNs may not be used as public identifiers, printed on mailed materials (with limited
                                          exceptions), or transmitted over the internet in unencrypted form
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                        <span>SSNs cannot be used as the sole means of authentication without additional factors</span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-indigo-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Destruction of Records</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          When disposing of records containing personal information, businesses must take
                                          reasonable steps to destroy the information (e.g., shredding, erasing, or otherwise
                                          making it unreadable)
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Breach Notification Obligations</h4>
                                <p className="text-gray-700 mb-3">
                                  A "security breach" generally means unauthorized access to or acquisition of unencrypted or
                                  un-redacted personal information that creates a risk of identity theft or other harm.
                                </p>
                                <p className="text-gray-700 mb-3">If a breach occurs, covered entities must:</p>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>Conduct a prompt investigation</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Notify affected North Carolina residents "without unreasonable delay" once they determine
                                      misuse is likely, consistent with law enforcement or remedial needs
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>Notify consumer reporting agencies if a large number of residents are affected</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>For certain entities, notify the NC Attorney General / Department of Justice</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Notices must include specific content such as the type of incident, kinds of information
                                      involved, and steps consumers can take
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Massachusetts Content */}
                          {state.id === 'massachusetts' && (
                            <div className="space-y-6">
                              <p className="text-gray-700">
                                Massachusetts is one of the most cited examples of a strict statewide cybersecurity rule:{' '}
                                <strong>201 CMR 17.00 – Standards for the Protection of Personal Information of Residents of the Commonwealth</strong>.
                                These regulations establish minimum standards for any person or business that owns or
                                licenses "personal information" about Massachusetts residents.
                              </p>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Who is covered?</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Any person or entity engaged in commerce that owns or licenses personal information about
                                      a Massachusetts resident in connection with providing goods or services or employment
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      This includes many financial institutions, lenders, mortgage companies, and service providers
                                      handling MA resident data
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Core Cybersecurity Program Requirements</h4>
                                <p className="text-gray-700 mb-3">
                                  201 CMR 17.00 requires covered entities to implement and maintain a comprehensive written
                                  information security program (WISP) that is:
                                </p>

                                <div className="space-y-4">
                                  <div className="bg-white rounded-lg p-4 border-l-4 border-purple-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Customized and Risk-Based</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>"Appropriate to the size, scope, and type of business"</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Takes into account the amount and sensitivity of personal information, the need for
                                          security, and the business's resources
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-purple-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Administrative Safeguards</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Designate one or more employees to maintain the security program</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Perform ongoing risk assessment of internal and external threats</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Develop policies for storage, access, and transportation of personal information</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Impose disciplinary measures for violations</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Oversee service providers and require contractual safeguards</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Review the WISP at least annually or when there is a material change in business
                                          practices
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-purple-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Technical Safeguards</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Secure user authentication (password policies, access controls)</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Unique IDs and access logging</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Encryption of personal information transmitted over public networks and, to the extent
                                          technically feasible, on portable devices and in certain storage contexts
                                        </span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Up-to-date firewall protection and security patches</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Malware protection and system monitoring for unauthorized use</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Reasonable system security for wireless networks</span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-purple-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Physical Safeguards</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Restrict physical access to records containing personal information</span>
                                      </li>
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                                        <span>Secure storage, destruction, and disposal practices</span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Breach and Enforcement</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      201 CMR 17.00 ties into Massachusetts' broader data breach law, which requires
                                      notification to affected residents and the Attorney General in the event of a security
                                      breach involving personal information
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Non‑compliance can be treated as an unfair or deceptive practice under Massachusetts
                                      consumer protection law, potentially leading to enforcement actions
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Oregon Content */}
                          {state.id === 'oregon' && (
                            <div className="space-y-6">
                              <p className="text-gray-700">
                                Oregon regulates data security and breach notification largely through the{' '}
                                <strong>Oregon Consumer Information Protection Act (OCIPA)</strong>, codified at ORS
                                646A.600–646A.628. The law requires organizations to safeguard certain personal information of
                                Oregon residents and to notify consumers (and in some cases the Oregon Department of Justice)
                                when breaches occur.
                              </p>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Who is covered?</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Any person or entity that owns, licenses, or maintains personal information about Oregon
                                      residents in connection with business or governmental operations
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Includes financial institutions, mortgage lenders, servicers, and other companies
                                      storing consumer data
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Security and Program Expectations</h4>
                                <p className="text-gray-700 mb-3">
                                  While OCIPA focuses heavily on breach response and notification, it also expects reasonable
                                  security practices:
                                </p>

                                <div className="space-y-4">
                                  <div className="bg-white rounded-lg p-4 border-l-4 border-green-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Reasonable Safeguards</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Businesses must implement and maintain reasonable security procedures and practices
                                          to protect personal information from unauthorized access, acquisition, destruction,
                                          use, modification, or disclosure
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-green-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Vendor Management</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          If an organization discloses personal information to a third party, it must ensure
                                          that the third party also maintains reasonable security safeguards
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-green-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Incident Response Planning</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          State guidance emphasizes that agencies and organizations should have written incident
                                          response plans and coordinate with applicable regulations when handling security incidents
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Breach Notification Obligations</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      If a "breach of security" involving Oregon consumers' personal information occurs,
                                      businesses must notify affected consumers within 45 days of discovering the breach,
                                      with limited exceptions (e.g., law enforcement delays)
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      If more than 250 Oregon residents are affected, the organization must also notify the
                                      Oregon Department of Justice, providing a copy of the consumer notice and additional
                                      details
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Specific content and methods of notification are prescribed, and credit monitoring must
                                      sometimes be offered, depending on the nature of the breach
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Washington Content */}
                          {state.id === 'washington' && (
                            <div className="space-y-6">
                              <p className="text-gray-700">
                                Washington has several data security and breach rules. For insurance licensees, the{' '}
                                <strong>Office of the Insurance Commissioner (OIC)</strong> enforces specific security breach
                                notification obligations, including under WAC 284-04-625 and related guidance. More broadly,
                                Washington has breach notification laws applicable to many businesses that handle personal
                                information of Washington residents.
                              </p>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Who is covered (insurance focus)?</h4>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                                    <span>Insurers, producers, and other licensees regulated by the Washington OIC</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      These entities must protect "nonpublic personal financial information" and private consumer
                                      information and follow specific breach reporting procedures
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Key Cybersecurity and Privacy Expectations</h4>

                                <div className="space-y-4">
                                  <div className="bg-white rounded-lg p-4 border-l-4 border-teal-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Safeguarding Consumer Information</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          Licensees must adopt administrative, technical, and physical safeguards appropriate
                                          to the sensitivity of consumer information they handle, consistent with privacy and
                                          security obligations under both state and federal law
                                        </span>
                                      </li>
                                    </ul>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 border-l-4 border-teal-600">
                                    <h5 className="font-semibold text-gray-900 mb-2">Vendor and Third-Party Oversight</h5>
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                                        <span>
                                          As in other states, insurers and licensees are expected to ensure that service
                                          providers maintain appropriate safeguards for consumer information
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">Security Breach Notification – Insurance Entities</h4>
                                <p className="text-gray-700 mb-3">
                                  If a security breach involves private consumer information (such as Social Security numbers
                                  or comparable identifiers) and is reasonably likely to expose customers to a risk of
                                  criminal activity, insurers and producers must:
                                </p>
                                <ul className="space-y-2 text-gray-700">
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Notify the Insurance Commissioner in writing within two business days after determining
                                      that notification to consumers must be sent
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                                    <div>
                                      <span className="font-semibold">Provide details such as:</span>
                                      <ul className="mt-2 space-y-1 ml-4">
                                        <li className="flex items-start gap-2">
                                          <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                                          <span>Number of consumers affected or potentially affected</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                          <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                                          <span>Nature of the information involved</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                          <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                                          <span>Steps the licensee is taking to remediate and protect consumers</span>
                                        </li>
                                      </ul>
                                    </div>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <i className="ri-checkbox-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                                    <span>
                                      Licensees must also comply with Washington's general data breach notification statute,
                                      which requires notification to affected residents and, for larger incidents, the
                                      Attorney General
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Help Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Help with State Compliance?</h2>
              <p className="text-lg text-gray-600 mb-8">
                Navigating state-specific cybersecurity requirements can be complex. Our team helps financial
                institutions understand and implement the controls needed to meet both federal and state regulations.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/schedule-call" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md whitespace-nowrap">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Compliance Consultation
                  </button>
                </a>
                <a href="/risk-assessment" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-blue-600 whitespace-nowrap">
                    <i className="ri-shield-check-line mr-2"></i>
                    Get a Compliance Assessment
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StateRequirementsPage;
